import React, { forwardRef, useCallback } from 'react';

import { Link, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { BaseSnackbar } from 'src/components';
import { Apollo } from 'src/gql/generated';
import { NotificationsActionType, useNotificationDispatch } from 'src/notifications';
import { alineLinkButtonStylesWithUnderline } from 'src/theme';
import { makeStyles } from 'tss-react/mui';
import { v4 as uuid4 } from 'uuid';

const useStyles = makeStyles()({
  linkButton: {
    ...alineLinkButtonStylesWithUnderline,
    '&:disabled': {
      cursor: 'default',
      opacity: 0.6,
    },
  },
});

interface UpgradeRequiredSnackbarProps {
  id: string;
  message?: string;
}

export const UpgradeRequiredSnackbar = forwardRef<HTMLDivElement, UpgradeRequiredSnackbarProps>(
  function UpgradeRequiredSnackbar(
    { id, message = 'You must upgrade to a Basic user or higher to access this functionality.' },
    ref
  ) {
    const { classes } = useStyles();
    const dispatch = useNotificationDispatch();
    const { closeSnackbar } = useSnackbar();
    const [requestUpgrade, { loading }] = Apollo.useRequestUpgradeMutation();

    const handleRequestUpgrade = useCallback(async () => {
      await requestUpgrade({
        variables: {
          input: {
            requestedRole: Apollo.OrgRole.Basic,
          },
        },
      });

      closeSnackbar(id);
      dispatch({
        payload: {
          key: uuid4(),
          message: 'Request sent. Your admin has been notified.',
          variant: 'success',
        },
        type: NotificationsActionType.PUSH_NOTIFICATION,
      });
    }, [closeSnackbar, dispatch, id, requestUpgrade]);

    return (
      <BaseSnackbar
        ref={ref}
        action={
          <Link
            className={classes.linkButton}
            component="button"
            disabled={loading}
            type="button"
            variant="caption"
            onClick={handleRequestUpgrade}
          >
            Request Upgrade
          </Link>
        }
        id={id}
        variant="warning"
      >
        <Typography variant="caption">{message}</Typography>
      </BaseSnackbar>
    );
  }
);
